import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getAllNotifications,
  getNotificationById,
  createNotification,
  updateNotification,
  deleteNotification,
  getNotificationsByUserId,
  markNotificationAsRead
} from '../services/notificationService';
import Notification from '../components/Common/Notification';

export const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    if (!user && !storedUser) {
      console.error('User or token not available');
      return {};
    }

    const token = user?.token || JSON.parse(storedUser).token;
    return { Authorization: `Bearer ${token}` };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllNotifications = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAllNotifications(authHeaders);
      setNotifications(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching messages.';
      showNotification(errorMessage, true);
    }
  };

  const fetchNotificationById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getNotificationById(id, authHeaders);
      setSelectedNotification(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching message with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const fetchNotificationsByUserId = async (id, userType, email) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getNotificationsByUserId(id, userType, email, authHeaders);
      setNotifications(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching messages for user ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addNotification = async (notificationData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newNotification = await createNotification(notificationData, authHeaders);
      setNotifications(prevNotifications => [...prevNotifications, newNotification]);
      showNotification('Message added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding message.';
      showNotification(errorMessage, true);
    }
  };

  const editNotification = async (id, notificationData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedNotification = await updateNotification(id, notificationData, authHeaders);
      setNotifications(prevNotifications =>
        prevNotifications.map(notification => (notification.id === id ? updatedNotification : notification))
      );
      showNotification('Message updated successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating Message.';
      showNotification(errorMessage, true);
    }
  };

  const removeNotification = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteNotification(id, authHeaders);
      setNotifications(prevNotifications => prevNotifications.filter(notification => notification.id !== id));
      showNotification('Message removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing Message.';
      showNotification(errorMessage, true);
    }
  };

  const markAsRead = async (id, email) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedNotification = await markNotificationAsRead(id, email, authHeaders); // Pass email to service
      setNotifications(prevNotifications =>
        prevNotifications.map(notification => (notification.id === id ? updatedNotification : notification))
      );
      showNotification('Message marked as read successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error marking Message as read.';
      showNotification(errorMessage, true);
    }
  };


  // useEffect(() => {
  //   if (user) {
  //     fetchNotificationsByUserId(user.id, user.role, user.email);
  //   }
  // }, [user]);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        selectedNotification,
        fetchAllNotifications,
        fetchNotificationById,
        fetchNotificationsByUserId,
        addNotification,
        editNotification,
        removeNotification,
        markAsRead,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </NotificationContext.Provider>
  );
};
